<template>
  <BaseLayout>
    <BaseDashboard :is-loading="isLoading">
      <div class="d-flex flex-column justify-content-between h-100 overflow-hidden">
        <div class="page-header px-5 py-3 " />
        <div class="flex-grow-1 overflow-auto">
          <div class="table-responsive mb-0 mt-3">
            <b-table
              ref="campaigns"
              class="table-vcenter text-nowrap"
              :class="{ 'h-100': sharedCampaignGroups && sharedCampaignGroups.length === 0 }"
              tbody-tr-class="campaign-table-row"
              details-td-class="p-0"
              :striped="false"
              :bordered="false"
              :outlined="false"
              :hover="true"
              :items="getSharedCampaignGroups"
              :fields="getHeaders"
              :busy.sync="isBusy"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              show-empty
              data-test="shared-campaigns-list"
            >
              <template v-slot:table-colgroup="scope">
                <col
                  v-for="field in scope.fields"
                  :key="field.key"
                  :style="{ width: field.width ? field.width : 'auto' }"
                >
              </template>
              <template #cell(expand)="data">
                <div
                  ref="expand-column"
                  id="expand-column"
                  class="mb-0"
                >
                  <div
                    v-if="data.item.hasCustomGroup"
                  >
                    <i
                      class="far"
                      :class="data.detailsShowing ? 'fa-chevron-down' : 'fa-chevron-right'"
                      @click="data.toggleDetails"
                    />
                  </div>
                </div>
              </template>
              <template v-slot:cell(name)="data">
                <div
                  ref="campaign-name-column"
                >
                  <span
                    v-if="data.item.hasCustomGroup"
                    class="ellipsis"
                    data-test="campaign-title"
                  >
                    {{ data.value }}
                  </span>
                  <span
                    class="ellipsis"
                    data-test="campaign-title"
                    v-else
                  >
                    <span
                      v-if="data.item.campaigns[0].shareTypes.includes('CAMPAIGN')"
                    >
                      <router-link
                        v-if="data.item.campaigns[0].shareTypes.includes('CAMPAIGN')"
                        :data-test="getCampaignLinkParams(data.item.campaigns[0]).dataTest"
                        class="large"
                        :to="{
                          name: `${getCampaignLinkParams(data.item.campaigns[0]).name}`,
                          params: {
                            campaignId: data.item.campaigns[0].id,
                            advertiserId: data.item.advertiserId
                          }
                        }"
                      >
                        {{ data.value }}
                      </router-link>
                    </span>
                    <span v-else>
                      {{ data.value }}
                    </span>
                  </span>
                  <div
                    class="small text-muted"
                    title="Advertiser Name"
                    data-test="campaign-advertiser-name"
                  >
                    {{ getAdvertiserName(data.item.advertiserId) }}
                  </div>
                </div>
              </template>
              <template v-slot:cell(state)="data">
                <div
                  ref="state-column"
                  v-if="data.item.state"
                >
                  <span
                    :data-test="`campaign-status-${data.value}`"
                  >
                    <span :class="[getStatusIcon(data), getStatusColor(data)]" />
                    {{ getColumnState(data) }}
                  </span>
                  <span v-if="!data.item.hasCustomGroup && (data.item.campaigns[0].shareTypes.includes('CAMPAIGN') && shouldShowPlayOrPauseButton(data.item))">
                    <a
                      href="#"
                      class="icon ml-3"
                      data-test="campaign-status-pause"
                      @click="updateCampaignState(data.item)"
                    >
                      <i :class="['fa-light', data.item.state === 'LIVE' ? 'fa-pause' : 'fa-play']" />
                    </a>
                  </span>
                </div>
              </template>
              <template v-slot:cell(startDate)="data">
                <div
                  ref="start-date-column"
                >
                  <small>{{ formatDateForPlatform(data.value) }}</small>
                </div>
              </template>
              <template v-slot:cell(endDate)="data">
                <div
                  ref="end-date-column"
                >
                  <small>{{ formatDateForPlatform(data.value) }}</small>
                </div>
              </template>
              <template v-slot:cell(budget)="data">
                <div
                  ref="budget-column"
                >
                  <div
                    v-if="data.item.budget"
                  >
                    <div class="clearfix">
                      <div
                        class="float-left"
                        data-test="budget-spent-percentage"
                      >
                        <strong>{{ getBudgetSpentPercentage(data.item.budgetRemaining) || '0' }}%</strong>
                      </div>
                    </div>
                    <div class="progress progress-xs">
                      <div
                        class="progress-bar"
                        :class="setProgressColor(getBudgetSpentPercentage(data.item.budgetRemaining)
                          || 0)"
                        role="progressbar"
                        :style="`width: ${getBudgetSpentPercentage(data.item.budgetRemaining) || '0'}%`"
                        :aria-valuenow="getBudgetSpentPercentage(data.item.budgetRemaining) || '0'"
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                  </div>
                </div>
              </template>
              <template v-slot:cell(impressions)="data">
                <div
                  ref="impressions-column"
                >
                  {{ data.value }}
                </div>
              </template>
              <template v-slot:cell(clicks)="data">
                <div
                  ref="clicks-column"
                >
                  {{ data.value }}
                </div>
              </template>
              <template v-slot:cell(ctr)="data">
                <div
                  ref="ctr-column"
                >
                  {{ data.value }}
                </div>
              </template>
              <template v-slot:cell(action)="data">
                <div
                  ref="actions-column"
                >
                  <div
                    v-if="!data.item.hasCustomGroup"
                  >
                    <CampaignShareActions
                      :index="data.index"
                      :item="data.item.campaigns[0]"
                      @refreshTable="refreshTable"
                    />
                  </div>
                  <div v-else>
                    <CampaignGroupActions
                      v-if="showGroupOptions(data.item)"
                      :index="data.index"
                      :advertiser-id="data.item.advertiserId"
                      :group="data.item.name"
                      :campaigns="data.item.campaigns"
                      :is-my-dashboard="isMyDashboard"
                      :are-all-campaigns-full-access="doAllCampaignsHaveFullAccess(data.item)"
                      @refreshTable="refreshTable"
                    />
                  </div>
                </div>
              </template>
              <template #row-details="{item}">
                <div
                  class="bg-light"
                >
                  <SharedCampaignsTable
                    :campaigns="item.campaigns"
                    :headers="getHeaders"
                    :shared-campaign-list="sharedCampaignList"
                    @refreshTable="refreshTable"
                  />
                </div>
              </template>
              <template v-slot:table-busy>
                <div
                  class="text-center d-flex flex-column align-items-center justify-content-center"
                  style="min-height: 400px;"
                >
                  <div class="loader mx-auto mb-3" />
                  <h6>Campaigns Loading</h6>
                </div>
              </template>
              <template v-slot:empty>
                <div class="row justify-content-md-center py-5">
                  <div
                    class="text-center"
                  >
                    <div>
                      <p
                        class="font-weight-bold intro-message text-center"
                      >
                        No campaigns have been shared
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </BaseDashboard>
  </BaseLayout>
</template>

<script>
import {
  mapActions, mapState, mapMutations, mapGetters,
} from 'vuex';
import * as types from '@/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as accountTypes from '@/modules/account/store/types';

import currenciesMixin from '@/core/mixins/currencies';
import dateHelper from '@/core/mixins/dateHelper';
import tableMixin from '@/modules/campaigns/mixins/tableMixin';

import tableHeaders from '@/views/Campaign/tableHeaders';
import CampaignShareActions from '@/modules/campaigns/components/CampaignShareActions';
import CampaignGroupActions from '@/modules/campaigns/components/CampaignGroupActions';

import SharedCampaignsTable from './SharedCampaignsTable.vue';

export default {
  name: 'ShareDashboard',
  components: {
    SharedCampaignsTable,
    CampaignShareActions,
    CampaignGroupActions,
  },
  mixins: [
    currenciesMixin,
    dateHelper,
    tableMixin,
  ],
  data() {
    return {
      ...tableHeaders,
      isBusy: true,
      sharedCampaignList: [],
      sharedCampaignGroups: [],
      sortBy: '',
      sortDesc: false,
      isMyDashboard: true,
    };
  },
  methods: {
    ...mapActions('account', [
      accountTypes.FETCH_SHARED_ADVERTISERS,
    ]),
    ...mapActions('campaigns', [
      campaignTypes.FETCH_CURRENCIES,
      campaignTypes.FETCH_SHARED_CAMPAIGN_GROUPS_WITH_CAMPAIGNS,
      campaignTypes.SET_CAMPAIGN_STATE,
    ]),
    ...mapMutations('campaigns', [
      campaignTypes.SET_IS_LOADING,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    shouldShowPlayOrPauseButton(campaign) {
      return campaign.state === 'LIVE' || campaign.state === 'PAUSED';
    },
    updateCampaignState(item) {
      this.isBusy = true;
      this.SET_CAMPAIGN_STATE({
        advertiserId: item.advertiserId,
        campaignId: item.id,
        state: item.state === 'LIVE' ? 'PAUSED' : 'LIVE',
      })
        .then((response) => {
          this.isBusy = false;
          this.sharedCampaignList
            .find((campaign) => campaign.id === response.id).state = response.state;
          this.SET_GLOBAL_MESSAGE({
            message: `Campaign was set to ${item.state.toLowerCase()}.`,
            type: 'info',
          });
        })
        .catch(() => {
          this.isBusy = false;
          this.SET_GLOBAL_MESSAGE({
            message: 'Campaign state could not be updated',
            type: 'danger',
          });
        });
    },
    async getSharedCampaignGroups() {
      return this.FETCH_SHARED_CAMPAIGN_GROUPS_WITH_CAMPAIGNS()
        .then((response) => {
          this.isBusy = false;
          this.sharedCampaignGroups = response[0];
          this.attachResizeObserver();
          return response[0] || [];
        });
    },
    getAdvertiserName(id) {
      const advertiser = this.sharedAdvertisers
        .find((item) => (item.id === id));
      if (advertiser && advertiser.name) {
        return advertiser.name;
      }
      return '';
    },
    refreshTable() {
      this.$refs.campaigns.refresh();
    },
    showGroupOptions(group) {
      return group.campaigns.every((item) => item.shareTypes.includes('CAMPAIGN') || item.shareTypes.includes('REPORTING'));
    },
    doAllCampaignsHaveFullAccess(group) {
      return group.campaigns.every((item) => item.shareTypes.includes('CAMPAIGN'));
    },
  },
  computed: {
    ...mapState('campaigns', [
      'campaigns',
      'headers',
      'isLoading',
      'currencies',
    ]),
    ...mapState('account', {
      accountAdvertiserId: 'advertiserId',
    }),
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    getHeaders() {
      this.assignRowDetailsColumnsWidth();
      return [
        ...this.sharedCampaignListHeaders,
        ...this.suppressedList,
        ...this.actionsHeader,
      ];
    },
  },
  async mounted() {
    if (this.currencies.length === 0) this.FETCH_CURRENCIES();
    this.sharedAdvertisers = await this.FETCH_SHARED_ADVERTISERS();
    await this.getSharedCampaignGroups();
    this.refreshTable();
    this.isMyDashboard = this.$route.meta.type === 'MY_DASHBOARD';
  },
};
</script>

<style lang="scss">
.table-responsive {
  min-height: 300px;
}
.page-header {
  margin: 0;
}
.pulse {
  box-shadow: 0 0 0 rgba(94, 186, 0, 0.4);
  animation: pulse 2s infinite;
}
@-webkit-keyframes pulse {
  0% {
    -webkit-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0.4);
  }
  70% {
    -webkit-box-shadow: 0 0 0 10px rgba(94, 186, 0, 0);
  }
  100% {
    -webkit-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0);
  }
}
@keyframes pulse {
  0% {
    -moz-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0.4);
    box-shadow: 0 0 0 0 rgba(94, 186, 0, 0.4);
  }
  70% {
    -moz-box-shadow: 0 0 0 10px rgba(94, 186, 0, 0);
    box-shadow: 0 0 0 10px rgba(94, 186, 0, 0);
  }
  100% {
    -moz-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0);
    box-shadow: 0 0 0 0 rgba(94, 186, 0, 0);
  }
}
</style>

<style lang="scss" scoped>
.intro-message {
  font-size: 1.5rem;
}
.full-width {
  width: 100% !important;
}
.custom-height-dropdown {
  height: 33px;
}

#expand-column {
  width: 64px;
}
</style>
